import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartTotal, sessionId } = useCart();
  const { isAuthenticated, user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  
  const [shippingAddress, setShippingAddress] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    session_id: sessionId
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const shipping = cartTotal >= 100 ? 0 : 10;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckout = async () => {
    // Validate form
    const required = ['firstName', 'lastName', 'email', 'address', 'city', 'state', 'zipCode'];
    const missing = required.filter(field => !shippingAddress[field]);
    
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          size: item.size,
          color: item.color
        })),
        shipping_address: {
          first_name: shippingAddress.firstName,
          last_name: shippingAddress.lastName,
          address: shippingAddress.address,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zip_code: shippingAddress.zipCode,
          country: shippingAddress.country,
          session_id: sessionId
        },
        payment_method: paymentMethod,
        is_guest: !isAuthenticated,
        guest_email: !isAuthenticated ? shippingAddress.email : null
      };

      const headers = isAuthenticated ? { Authorization: `Bearer ${token}` } : {};

      if (paymentMethod === 'stripe') {
        const response = await axios.post(
          `${API}/checkout/stripe/create-session`,
          orderData,
          { headers }
        );
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else {
        toast.info('PayPal checkout coming soon');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="checkout-page">
      <div className="container-custom">
        <h1 className="font-['Syne'] text-3xl md:text-4xl font-bold mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Shipping Form */}
          <div>
            <h2 className="font-['Syne'] text-xl font-bold mb-6">Shipping Information</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={shippingAddress.firstName}
                    onChange={handleInputChange}
                    className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                    data-testid="checkout-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={shippingAddress.lastName}
                    onChange={handleInputChange}
                    className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                    data-testid="checkout-lastname"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={shippingAddress.email}
                  onChange={handleInputChange}
                  className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                  data-testid="checkout-email"
                />
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  name="address"
                  value={shippingAddress.address}
                  onChange={handleInputChange}
                  className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                  data-testid="checkout-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={shippingAddress.city}
                    onChange={handleInputChange}
                    className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                    data-testid="checkout-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={shippingAddress.state}
                    onChange={handleInputChange}
                    className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                    data-testid="checkout-state"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="zipCode">ZIP Code *</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    value={shippingAddress.zipCode}
                    onChange={handleInputChange}
                    className="mt-1 rounded-none bg-neutral-900 border-neutral-800"
                    data-testid="checkout-zipcode"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="country"
                    value={shippingAddress.country}
                    disabled
                    className="mt-1 rounded-none bg-neutral-900 border-neutral-800 opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mt-8">
              <h2 className="font-['Syne'] text-xl font-bold mb-6">Payment Method</h2>
              
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 p-4 border border-neutral-800 cursor-pointer hover:border-neutral-600">
                    <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                    <div className="flex items-center space-x-2">
                      <CreditCard size={20} />
                      <span>Credit / Debit Card (Stripe)</span>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-neutral-800 cursor-pointer hover:border-neutral-600 opacity-50">
                    <RadioGroupItem value="paypal" id="paypal" disabled data-testid="payment-paypal" />
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-blue-500">PayPal</span>
                      <span className="text-xs text-muted-foreground">(Coming Soon)</span>
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="border border-neutral-800 p-6 sticky top-24">
              <h2 className="font-['Syne'] text-xl font-bold mb-6">Order Summary</h2>
              
              {/* Items */}
              <div className="space-y-4 pb-6 border-b border-neutral-800">
                {cart.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <img
                      src={item.product?.images?.[0] || 'https://via.placeholder.com/60'}
                      alt={item.product?.name}
                      className="w-16 h-16 object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium">{item.product?.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {item.color} / {item.size} x {item.quantity}
                      </p>
                    </div>
                    <span className="text-sm">
                      {formatPrice((item.product?.price || 0) * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 py-6 border-b border-neutral-800">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatPrice(tax)}</span>
                </div>
              </div>

              <div className="flex justify-between py-4 text-lg font-bold">
                <span>Total</span>
                <span data-testid="checkout-total">{formatPrice(total)}</span>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="btn-primary rounded-none w-full"
                data-testid="place-order-btn"
              >
                {loading ? 'Processing...' : `Pay ${formatPrice(total)}`}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Your payment is secured by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
